import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SESSION_TYPES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  session_type: "",
  event_date: "",
  location: "",
  message: "",
};

const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_PHOTO_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
];

const fieldClass =
  "bg-transparent border-0 border-b border-ink/20 rounded-none px-0 h-12 text-ink placeholder:text-inkmuted focus-visible:ring-0 focus-visible:border-ink transition-colors";

export const ContactForm = ({ testIdPrefix = "contact" }) => {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const fileInputRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      toast.error("Please attach an image (JPG, PNG, WEBP, HEIC or GIF).");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("That photo is over 10 MB. Try a smaller one.");
      e.target.value = "";
      return;
    }
    setPhoto(file);
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.session_type || !form.message) {
      toast.error("Please fill in your name, email, session type and message.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (photo) data.append("photo", photo);
      await axios.post(`${API}/inquiries`, data);
      setSubmittedName(form.name);
      setForm(EMPTY);
      clearPhoto();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or email me directly.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submittedName) {
    const first = submittedName.split(" ")[0] || "friend";
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className="border-l-2 border-sage pl-8 md:pl-12 py-10 md:py-14"
      >
        <div className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-sage/15 text-sage mb-6">
          <Check size={20} strokeWidth={1.75} />
        </div>
        <p className="overline text-sage mb-3">Confirmation received</p>
        <h3 className="font-serif font-light text-ink text-3xl md:text-5xl leading-[1.05] tracking-tight">
          Thanks, {first}.
        </h3>
        <p className="mt-6 text-inksoft leading-relaxed max-w-xl">
          Your note landed safely. I'll be in touch within <span className="text-ink">24–48 business hours</span> — a confirmation is on its way to your inbox now (if you don't see it soon, peek at your spam folder just in case).
        </p>
        <p className="mt-4 text-inksoft leading-relaxed max-w-xl">
          In the meantime, feel free to wander the <span className="italic">portfolio</span> or peek at the <span className="italic">journal</span>. Talk soon.
        </p>
        <button
          type="button"
          onClick={() => setSubmittedName("")}
          data-testid={`${testIdPrefix}-send-another`}
          className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-ink hover:text-sage transition-colors border-b border-ink/30 hover:border-sage pb-0.5"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Your name *</Label>
          <Input
            data-testid={`${testIdPrefix}-name`}
            className={fieldClass}
            placeholder="Jane Doe"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Email *</Label>
          <Input
            data-testid={`${testIdPrefix}-email`}
            type="email"
            className={fieldClass}
            placeholder="jane@email.com"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Phone</Label>
          <Input
            data-testid={`${testIdPrefix}-phone`}
            className={fieldClass}
            placeholder="(555) 000-0000"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Session type *</Label>
          <Select value={form.session_type} onValueChange={(v) => update("session_type", v)}>
            <SelectTrigger data-testid={`${testIdPrefix}-session-type`} className={fieldClass}>
              <SelectValue placeholder="Choose a session" />
            </SelectTrigger>
            <SelectContent>
              {SESSION_TYPES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`session-opt-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Ideal date</Label>
          <Input
            data-testid={`${testIdPrefix}-date`}
            type="date"
            className={fieldClass}
            value={form.event_date}
            onChange={(e) => update("event_date", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="overline text-inkmuted">Location</Label>
          <Input
            data-testid={`${testIdPrefix}-location`}
            className={fieldClass}
            placeholder="Studio, home, or on-location"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="overline text-inkmuted">Tell me about your session *</Label>
        <Textarea
          data-testid={`${testIdPrefix}-message`}
          rows={5}
          className="bg-transparent border-0 border-b border-ink/20 rounded-none px-0 text-ink placeholder:text-inkmuted focus-visible:ring-0 focus-visible:border-ink resize-none transition-colors"
          placeholder="Who's in the session, the mood you're after, anything you're excited about..."
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
      </div>

      {/* Optional photo attachment */}
      <div className="space-y-3">
        <Label className="overline text-inkmuted">Attach a photo (optional)</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
          onChange={handlePhotoChange}
          className="hidden"
          data-testid={`${testIdPrefix}-photo-input`}
        />
        {!photo ? (
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid={`${testIdPrefix}-photo-pick`}
            className="group inline-flex items-center gap-3 text-sm text-ink border-b border-ink/30 hover:border-sage hover:text-sage pb-1 transition-colors"
          >
            <Paperclip size={15} strokeWidth={1.75} />
            Choose a photo to send with your inquiry
          </button>
        ) : (
          <div
            data-testid={`${testIdPrefix}-photo-preview`}
            className="flex items-start gap-4 pt-1"
          >
            <img
              src={URL.createObjectURL(photo)}
              alt="Attachment preview"
              className="h-20 w-20 object-cover border border-ink/15"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink truncate">{photo.name}</p>
              <p className="text-xs text-inkmuted mt-0.5">
                {(photo.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <button
                type="button"
                onClick={clearPhoto}
                data-testid={`${testIdPrefix}-photo-remove`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs tracking-[0.14em] uppercase text-inkmuted hover:text-ink transition-colors"
              >
                <X size={13} strokeWidth={1.75} />
                Remove photo
              </button>
            </div>
          </div>
        )}
        <p className="text-xs text-inkmuted">
          JPG, PNG, WEBP, HEIC or GIF · up to 10&nbsp;MB
        </p>
      </div>

      <button
        type="submit"
        data-testid={`${testIdPrefix}-submit-button`}
        disabled={loading}
        className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-10 py-4 text-sm font-medium hover:bg-sage disabled:opacity-60 transition-colors"
      >
        {loading ? "Sending..." : "Send inquiry"}
        <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export default ContactForm;
